import { useState, useEffect, useMemo } from 'react';
import { useStepContext } from '../contexts/stepContext';
import { useSubscriptionContext } from '../contexts/subscriptionContext';
import { LocaleText, useMakeLocalizedText } from '../LocaleText';
import { Footer } from '../components/Footer';
import { useTrack } from '../hooks';
import { CancelReason } from '../types';
import type { SurveyNode } from '../types';
import { shuffle } from 'lodash';
import { DEFAULT_CANCEL_REASON, DEFAULT_CANCEL_REASON_CODE } from '../constants';

export const SurveyStep = ({ step }: { step: SurveyNode }) => {
  const { dispatch } = useStepContext();
  const track = useTrack();
  const subscription = useSubscriptionContext();
  const getLocalizedText = useMakeLocalizedText();

  const [selectedReason, setSelectedReason] = useState<CancelReason | null>(null);
  const [otherText, setOtherText] = useState<string>('');

  // disable lint warning, cancelReasons should be stable until the config is updated (preview-mode only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cancelReasons = step.parameters.cancelReasons;
  const reasonCodesKey = cancelReasons.map(r => r.code).join(',');

  // Shuffle reasons (except code '1' which is "Other")
  // Keyed by codes string so copy-only updates don't reshuffle (required for editor preview)
  const shuffledCodes = useMemo(() => {
    const codes = reasonCodesKey.split(',').filter(Boolean);
    const shuffled = shuffle(codes.filter(c => c !== '1' && c !== DEFAULT_CANCEL_REASON_CODE));
    if (codes.includes('1')) shuffled.push('1');
    return shuffled;
  }, [reasonCodesKey]);

  const shuffledReasons = useMemo(
    () => shuffledCodes.map(code => cancelReasons.find(r => r.code === code)).filter(Boolean) as CancelReason[],
    [cancelReasons, shuffledCodes]
  );

  // Fire tracking event on mount
  useEffect(() => {
    track('subscriptioncancel_exitsurvey_shown', {
      subscriptionId: subscription.public_id,
      responseRequired: false
    });
  }, [subscription.public_id, track]);

  const handleNext = () => {
    let cancelReason = DEFAULT_CANCEL_REASON;
    if (selectedReason) {
      // Get localized text for the selected reason
      const localizedReasonText = getLocalizedText(`cancelReasons.${selectedReason.code}`);

      // Use "Other" text if provided, otherwise use localized reason text
      const cancelReasonText =
        selectedReason.code === '1' && otherText.trim().length > 0 ? otherText.trim() : localizedReasonText;

      cancelReason = `${selectedReason.code} | ${cancelReasonText}`;
    }

    // Fire tracking event
    track('subscriptioncancel_reason_selected', {
      subscriptionId: subscription.public_id,
      cancelReason
    });

    // Dispatch action with reason code and text
    dispatch({
      type: 'cancel-reason',
      cancelReason,
      reasonCode: selectedReason ? selectedReason.code : DEFAULT_CANCEL_REASON_CODE
    });
  };

  return (
    <div>
      <p>
        <LocaleText t="description" />
      </p>
      <div className="og-cancel-reasons">
        {shuffledReasons.map(reason => (
          <div key={reason.code}>
            <label>
              <input
                type="radio"
                name="cancel-reason"
                value={reason.code}
                onChange={() => setSelectedReason(reason)}
                checked={selectedReason?.code === reason.code}
              />{' '}
              <LocaleText t={`cancelReasons.${reason.code}`} />
            </label>
            {reason.code === '1' && selectedReason?.code === '1' && (
              <input
                type="text"
                className="og-cancel-reason-other"
                name="cancel_reason_details"
                placeholder="Cancel reason"
                aria-label="Cancel reason"
                value={otherText}
                onChange={e => setOtherText(e.target.value)}
              />
            )}
          </div>
        ))}
      </div>
      <Footer>
        <button onClick={handleNext} className="og-button" data-variant="primary">
          <LocaleText t="submit" />
        </button>
      </Footer>
    </div>
  );
};
